import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { date } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';

/**
 * Retro data campaigns: consent for personal data held before the Act (S.5(2)).
 *
 * The list is the manager's view: where each campaign is in its trail, how
 * many people it reached, and what share answered. Everything else lives on
 * the campaign's own page.
 */

export interface CampaignRow {
  id: string;
  name: string;
  noticeId: string;
  purposeIds: string[];
  lang: string;
  channel: 'email' | 'sms' | 'whatsapp';
  status: string;
  stage: 'draft' | 'audience_locked' | 'sending' | 'paused' | 'waiting' | 'reminder' | 'closed' | 'cancelled';
  subject: string;
  body: string;
  linkTtlHours: number;
  sendRatePerMinute: number;
  audience: { mode?: string; before?: string; identifierTypes?: string[]; limit?: number };
  channelId: string | null;
  reminderRule: { earliestDays: number; maxReminders: number; switchChannel: boolean };
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  audienceLockedAt: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closureDecision: string | null;
  closureAction: 'keep_s7' | 'stop_and_erase' | 'leave_open' | null;
  counts: Record<string, number>;
  waves: Array<{ id: string; number: number; kind: 'first' | 'reminder'; channel: string; recipients: number; startedAt: string; finishedAt: string | null; sent: number; failed: number }>;
  responseRate: number | null;
  lastActivityAt: string | null;
}

export interface PurposeRow { id: string; status: string; current: { name: string; basis: string } }
export interface NoticeRow {
  id: string;
  key: string;
  versions: Array<{ version: number; status: string; title: string; languages: string[] }>;
}
export interface ImportRow {
  id: string; name: string; source: 'file' | 'database'; detail: { fileName?: string; host?: string; query?: string };
  status: 'open' | 'done' | 'failed'; rowsReceived: number; peopleAdded: number; peopleKnown: number; rowsRejected: number;
  rejectedReasons: Record<string, number>; error: string | null; createdAt: string;
}

export interface ChannelRow {
  id: string; name: string; kind: string; medium: 'email' | 'sms' | 'whatsapp'; status: string; isDefault: boolean; summary: string;
  lastTestOk: boolean | null;
}

export const STAGE_LABEL: Record<CampaignRow['stage'], string> = {
  draft: 'Draft',
  audience_locked: 'Audience locked',
  sending: 'Sending',
  paused: 'Paused',
  waiting: 'Awaiting responses',
  reminder: 'Reminder going out',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const STAGE_TONE: Record<CampaignRow['stage'], 'ok' | 'warn' | 'bad' | 'brand' | undefined> = {
  draft: undefined, audience_locked: undefined, sending: 'brand', paused: 'warn',
  waiting: 'brand', reminder: 'brand', closed: 'ok', cancelled: 'bad',
};

export default function Campaigns() {
  const navigate = useNavigate();
  const writable = can(getUser(), 'campaigns:edit');
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);

  const campaigns = useResource<{ campaigns: CampaignRow[] }>(() => api.get('/v1/campaigns'), [reload]);
  const purposes = useResource<{ purposes: PurposeRow[] }>(() => api.get('/v1/purposes'), []);
  const notices = useResource<{ notices: NoticeRow[] }>(() => api.get('/v1/notices'), []);
  const channels = useResource<{ channels: ChannelRow[] }>(() => api.get('/v1/channels'), []);
  const langs = useResource<{ languages: string[]; catalogue: Array<{ code: string; endonym: string; english: string }> }>(
    () => api.get('/v1/settings/languages'), [],
  );
  const imports = useResource<{ imports: ImportRow[] }>(() => api.get('/v1/audience-imports'), [reload]);
  const [importing, setImporting] = useState<'file' | 'database' | null>(null);

  const rows = campaigns.data?.campaigns ?? [];
  const open = rows.filter((c) => c.stage !== 'closed' && c.stage !== 'cancelled');
  const reached = rows.reduce((n, c) => n + (c.counts.reached ?? 0), 0);
  const responded = rows.reduce((n, c) => n + (c.counts.responded ?? 0), 0);

  return (
    <>
      <PageHead title="Retro data campaigns">
        {writable && <button className="primary" onClick={() => setCreating(true)}>New campaign</button>}
      </PageHead>

      {rows.length > 0 && (
        <p className="page-line">
          <strong>{responded}</strong> of {reached} people reached have responded{reached ? ` (${Math.round((responded / reached) * 1000) / 10}%)` : ''} · {open.length} of {rows.length} campaigns in progress
        </p>
      )}

      <ErrorNote error={campaigns.error} />
      {campaigns.loading && !campaigns.data && <Loading what="Loading campaigns" />}

      {campaigns.data && (rows.length === 0 ? (
        <Card><Empty>No campaigns.</Empty></Card>
      ) : (
        <Card>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Stage</th>
                  <th className="num">Audience</th>
                  <th className="num">Reached</th>
                  <th className="num">Responded</th>
                  <th className="num">Response rate</th>
                  <th>Last activity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/campaigns/${c.id}`)}>
                    <td>
                      <strong>{c.name}</strong>
                      <div className="small muted">{c.channel === 'sms' ? 'SMS' : c.channel === 'whatsapp' ? 'WhatsApp' : 'Email'} · {c.purposeIds.length === 1 ? '1 purpose' : `${c.purposeIds.length} purposes`}{c.waves.length > 1 ? ` · ${c.waves.length - 1} reminder${c.waves.length === 2 ? '' : 's'}` : ''}</div>
                    </td>
                    <td><Pill tone={STAGE_TONE[c.stage]}>{STAGE_LABEL[c.stage]}</Pill></td>
                    <td className="num">{c.counts.total || (c.audience.limit ? `up to ${c.audience.limit}` : '')}</td>
                    <td className="num">{c.counts.reached ?? 0}</td>
                    <td className="num">{c.counts.responded ?? 0}</td>
                    <td className="num">{c.responseRate === null ? '' : `${c.responseRate}%`}</td>
                    <td className="small muted">{date(c.lastActivityAt)}</td>
                    <td style={{ textAlign: 'right' }}><Link className="btn small" to={`/campaigns/${c.id}`} onClick={(e) => e.stopPropagation()}>Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      <Card title="Audience imports">
        {imports.data && imports.data.imports.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead><tr><th>Import</th><th>Source</th><th className="num">People</th><th className="num">Existing</th><th className="num">Rejected</th><th>Date</th><th>Status</th></tr></thead>
              <tbody>
                {imports.data.imports.map((i) => (
                  <tr key={i.id}>
                    <td><strong>{i.name}</strong></td>
                    <td className="small">{i.source === 'file' ? `File${i.detail.fileName ? ` · ${i.detail.fileName}` : ''}` : `Database · ${i.detail.host ?? ''}`}</td>
                    <td className="num">{i.peopleAdded + i.peopleKnown}</td>
                    <td className="num">{i.peopleKnown}</td>
                    <td className="num" title={Object.entries(i.rejectedReasons).map(([k, v]) => `${v} ${k}`).join('; ')}>{i.rowsRejected}</td>
                    <td className="small muted">{date(i.createdAt)}</td>
                    <td>{i.status === 'done' ? <Pill tone="ok">Done</Pill> : i.status === 'failed' ? <Pill tone="bad">Failed</Pill> : <Pill tone="warn">In progress</Pill>}{i.error && <div className="small muted">{i.error}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {imports.data && imports.data.imports.length === 0 && <Empty>No imports.</Empty>}
        {can(getUser(), 'campaigns:import') && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={() => setImporting('file')}>Upload file</button>
            <button onClick={() => setImporting('database')}>Pull from database</button>
          </div>
        )}
      </Card>

      {importing === 'file' && <FileImportModal onClose={() => setImporting(null)} onDone={() => { setImporting(null); setReload((n) => n + 1); }} />}
      {importing === 'database' && <DatabaseImportModal onClose={() => setImporting(null)} onDone={() => { setImporting(null); setReload((n) => n + 1); }} />}

      {creating && (
        <CreateModal
          imports={(imports.data?.imports ?? []).filter((i) => i.status === 'done')}
          purposes={(purposes.data?.purposes ?? []).filter((p) => p.status === 'active' && p.current.basis === 'consent')}
          notices={notices.data?.notices ?? []}
          channels={channels.data?.channels ?? []}
          offered={langs.data?.languages ?? ['en']}
          catalogue={langs.data?.catalogue ?? []}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setReload((n) => n + 1);
            navigate(`/campaigns/${id}`);
          }}
        />
      )}
    </>
  );
}

export function CreateModal({
  purposes, notices, channels, offered, catalogue, imports, onClose, onCreated,
}: {
  imports: ImportRow[];
  purposes: PurposeRow[];
  notices: NoticeRow[];
  channels: ChannelRow[];
  offered: string[];
  catalogue: Array<{ code: string; endonym: string; english: string }>;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const published = notices.filter((n) => n.versions.some((v) => v.status === 'published'));

  const [name, setName] = useState('');
  const [noticeId, setNoticeId] = useState(published[0]?.id ?? '');
  const [purposeIds, setPurposeIds] = useState<string[]>([]);
  const [lang, setLang] = useState('en');
  const [channel, setChannel] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [channelId, setChannelId] = useState('');
  const [mode, setMode] = useState<'no_decision' | 'import'>(imports.length ? 'import' : 'no_decision');
  const [importId, setImportId] = useState(imports[0]?.id ?? '');
  const [limit, setLimit] = useState(1000);
  const [subject, setSubject] = useState('Your personal data: notice and consent preferences');
  const [body, setBody] = useState(
    'Dear Customer,\n\n'
    + 'Under the Digital Personal Data Protection Act, 2023, we are required to inform you of '
    + 'the personal data we hold about you and the purposes for which it is processed, and to '
    + 'obtain your consent.\n\n'
    + 'Please review the notice and record your preferences using the link below. The link is '
    + 'unique to you and valid for a single use:\n{{link}}\n\n'
    + 'You may change your preferences at any time.\n',
  );
  const [earliestDays, setEarliestDays] = useState(7);
  const [maxReminders, setMaxReminders] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noticeLangs = published.find((n) => n.id === noticeId)
    ?.versions.filter((v) => v.status === 'published').flatMap((v) => v.languages) ?? ['en'];
  const usableChannels = channels.filter((c) => c.medium === channel && c.status === 'active');

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ campaign: { id: string } }>('/v1/campaigns', {
        name, noticeId, purposeIds, lang, channel, subject, body,
        channelId: channelId || undefined,
        reminderRule: { earliestDays, maxReminders, switchChannel: false },
        audience: {
          mode,
          ...(mode === 'import' ? { importId } : {}),
          identifierTypes: [channel === 'email' ? 'email' : 'phone'], limit,
        },
      });
      onCreated(res.campaign.id);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  const ready = name.trim() && noticeId && purposeIds.length && body.includes('{{link}}') && (channel !== 'email' || subject.trim()) && (mode !== 'import' || importId);

  return (
    <Modal
      wide
      title="New campaign"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={create} disabled={busy || !ready}>
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </>
      }
    >
      <Field label="Name" help="For your team. Not shown to recipients.">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Members on file before the Act" />
      </Field>

      <div className="row">
        <Field label="Notice" help="Published notices only.">
          <select value={noticeId} onChange={(e) => setNoticeId(e.target.value)}>
            {published.map((n) => <option key={n.id} value={n.id}>{n.versions.find((v) => v.status === 'published')?.title ?? n.key}</option>)}
          </select>
        </Field>
        <Field label="Language" help="Languages the notice is published in.">
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            {[...new Set(noticeLangs)].map((code) => {
              const l = catalogue.find((x) => x.code === code);
              return <option key={code} value={code}>{l ? `${l.endonym} · ${l.english}` : code}{offered.includes(code) ? '' : ' (not offered)'}</option>;
            })}
          </select>
        </Field>
      </div>

      <Field label="Purposes">
        <div className="check-list">
          {purposes.map((p) => (
            <label key={p.id}>
              <input
                type="checkbox"
                checked={purposeIds.includes(p.id)}
                onChange={(e) => setPurposeIds((ids) => (e.target.checked ? [...ids, p.id] : ids.filter((x) => x !== p.id)))}
              />
              {p.current.name}
            </label>
          ))}
        </div>
      </Field>

      <div className="row">
        <Field label="Send by">
          <select value={channel} onChange={(e) => { setChannel(e.target.value as 'email' | 'sms' | 'whatsapp'); setChannelId(''); }}>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </Field>
        <Field label="Channel">
          <select value={channelId} onChange={(e) => setChannelId(e.target.value)}>
            <option value="">{usableChannels.find((c) => c.isDefault) ? 'The default channel' : 'Deployment default'}</option>
            {usableChannels.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.summary}</option>)}
          </select>
        </Field>
      </div>

      <div className="row">
        <Field label="Audience">
          <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
            {imports.length > 0 && <option value="import">Imported list</option>}
            <option value="no_decision">Existing records without a decision</option>
          </select>
        </Field>
        {mode === 'import' ? (
          <Field label="Import">
            <select value={importId} onChange={(e) => setImportId(e.target.value)}>
              {imports.map((i) => <option key={i.id} value={i.id}>{i.name} · {i.peopleAdded + i.peopleKnown}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Limit">
            <input type="number" min={1} max={100000} value={limit} onChange={(e) => setLimit(Number(e.target.value))} />
          </Field>
        )}
      </div>

      {channel === 'email' && (
        <Field label="Subject">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
      )}

      <Field label="Message" help="Must contain {{link}}.">
        <textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {!body.includes('{{link}}') && <div className="note warn">The message must contain {'{{link}}'}.</div>}

      <h2 className="section">Reminders</h2>
      <div className="row">
        <Field label="Earliest" help="Days after the previous contact.">
          <input type="number" min={1} max={90} value={earliestDays} onChange={(e) => setEarliestDays(Number(e.target.value))} />
        </Field>
        <Field label="Maximum" help="Per person.">
          <input type="number" min={0} max={5} value={maxReminders} onChange={(e) => setMaxReminders(Number(e.target.value))} />
        </Field>
      </div>

      <ErrorNote error={error} />
    </Modal>
  );
}

/** Reads a CSV or a one-per-line list in the browser and sends rows in chunks. Only identifiers travel. */
function parseIdentifiers(text: string): { rows: Array<{ identifier: string; identifierType?: 'email' | 'phone' }>; columns: string[] } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], columns: [] };
  const split = (l: string) => l.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ''));
  const first = split(lines[0]!);
  const headerish = first.some((c) => /email|phone|mobile|identifier|contact|type/i.test(c)) && !first.some((c) => c.includes('@') || /^\+?\d{10,}$/.test(c));
  const columns = headerish ? first : [];
  const idCol = headerish ? Math.max(0, first.findIndex((c) => /email|phone|mobile|identifier|contact/i.test(c))) : 0;
  const typeCol = headerish ? first.findIndex((c) => /^type$|identifier_type|id_type/i.test(c)) : -1;
  const rows = (headerish ? lines.slice(1) : lines).map((l) => {
    const cells = split(l);
    const identifier = cells[idCol] ?? '';
    const t = typeCol >= 0 ? (cells[typeCol] ?? '').toLowerCase() : '';
    return { identifier, ...(t === 'email' || t === 'phone' ? { identifierType: t as 'email' | 'phone' } : {}) };
  }).filter((r) => r.identifier);
  return { rows, columns };
}

function FileImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ReturnType<typeof parseIdentifiers> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function onFile(f: File | undefined) {
    if (!f) return;
    setFileName(f.name);
    const text = await f.text();
    setParsed(parseIdentifiers(text));
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
  }

  async function upload() {
    if (!parsed) return;
    const chunks: typeof parsed.rows[] = [];
    for (let i = 0; i < parsed.rows.length; i += 5000) chunks.push(parsed.rows.slice(i, i + 5000));
    try {
      setBusy(`Sending 1 of ${chunks.length}`);
      const first = await api.post<{ importRow: { id: string } }>('/v1/audience-imports/file', { name, fileName, columns: parsed.columns, rows: chunks[0] ?? [] });
      for (let i = 1; i < chunks.length; i += 1) {
        setBusy(`Sending ${i + 1} of ${chunks.length}`);
        await api.post(`/v1/audience-imports/${first.importRow.id}/rows`, { rows: chunks[i] });
      }
      const done = await api.post<{ import: ImportRow }>(`/v1/audience-imports/${first.importRow.id}/finish`);
      toast(`${done.import.peopleAdded + done.import.peopleKnown} people brought in${done.import.rowsRejected ? `, ${done.import.rowsRejected} rows rejected` : ''}.`, 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(null);
    }
  }

  return (
    <Modal
      title="Upload file"
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={!!busy}>Cancel</button><button className="primary" onClick={upload} disabled={!!busy || !parsed?.rows.length || !name.trim()}>{busy ?? 'Bring them in'}</button></>}
    >
      <Field label="File" help="CSV or one identifier per line. Optional column: email or phone.">
        <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => onFile(e.target.files?.[0])} />
      </Field>
      <Field label="Name" help="What this batch is, for the campaign that addresses it.">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Membership export, 2019 to 2023" />
      </Field>
      {parsed && (
        <div className="note">
          {parsed.rows.length} rows read{parsed.columns.length ? `, columns: ${parsed.columns.join(', ')}` : ''}.
          {parsed.rows.length > 0 && ` Identifiers look like: ${parsed.rows.slice(0, 2).map((r) => r.identifier.replace(/^(.).+(@.*)$/, '$1…$2').replace(/^(\+?\d{2})\d+(\d{2})$/, '$1…$2')).join(', ')}`}
        </div>
      )}
    </Modal>
  );
}

function DatabaseImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [connectionString, setConnectionString] = useState('');
  const [query, setQuery] = useState('SELECT email FROM members WHERE joined_on < \'2023-08-11\'');
  const [busy, setBusy] = useState(false);

  async function pull() {
    setBusy(true);
    try {
      const res = await api.post<{ importRow: ImportRow; stats: { added: number; known: number; rejected: number } }>('/v1/audience-imports/database', { name, connectionString, query });
      toast(`${res.stats.added + res.stats.known} people pulled${res.stats.rejected ? `, ${res.stats.rejected} rows rejected` : ''}.`, 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Pull from database"
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={busy}>Cancel</button><button className="primary" onClick={pull} disabled={busy || !name.trim() || !connectionString.trim() || !query.trim()}>{busy ? 'Pulling…' : 'Pull now'}</button></>}
    >
      <Field label="Name">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="CRM members view" />
      </Field>
      <Field label="Connection" help="Read-only user. Not stored. postgres://user:password@host:5432/database">
        <input type="password" value={connectionString} onChange={(e) => setConnectionString(e.target.value)} autoComplete="off" />
      </Field>
      <Field label="Query or view" help="Postgres. One SELECT, read-only, 60 seconds, 200,000 rows. First column: identifier. Second column, optional: email or phone.">
        <textarea rows={4} value={query} onChange={(e) => setQuery(e.target.value)} style={{ fontFamily: 'var(--mono)', fontSize: 12 }} />
      </Field>
    </Modal>
  );
}
